import { Alert, Box, LinearProgress, Typography } from "@mui/material";
import clsx from "clsx";
import type React from "react";
import { COMPONENTS_TRANSLATION_NAMESPACE } from "src/components/config";
import { LOADING_FRAME_TK } from "src/components/generics/config";
import { useTranslation } from "src/design-system/core/TranslationContext";
import type { UseQueryStatus } from "src/types/query";
import { useLoadingFrameStyles } from "./LoadingFrame.styles";

interface LoadingFrameProps {
    status: UseQueryStatus[];
    loadingMessage?: string;
    errorMessage?: string;
    displayLoading?: boolean;
    keepSameChildren?: boolean;
    withSmallMargin?: boolean;
    children?: React.ReactNode;
}

export function areAllSuccess(statusList: UseQueryStatus[]): boolean {
    return statusList.every((status) => status === "success");
}

export const LoadingFrame: React.FC<LoadingFrameProps> = ({
    status,
    loadingMessage,
    keepSameChildren,
    children,
    displayLoading = true,
    errorMessage: errorMessageProp,
    withSmallMargin = false,
}) => {
    const classes = useLoadingFrameStyles();
    const isPending = status.includes("pending");
    const { t } = useTranslation(`${COMPONENTS_TRANSLATION_NAMESPACE}:${LOADING_FRAME_TK}`);
    const errorMessage = errorMessageProp ?? t("loading-error");

    if (status.includes("error")) {
        return (
            <Box className={classes.error}>
                <Alert severity="error">{errorMessage}</Alert>
            </Box>
        );
    }

    return (
        <>
            {isPending && displayLoading && (
                <Box marginTop={withSmallMargin ? 5 : 40}>
                    {loadingMessage && (
                        <Box mt={-5} mb={2} textAlign="center">
                            <Typography className={classes.message}>{loadingMessage}</Typography>
                        </Box>
                    )}
                    <LinearProgress />
                </Box>
            )}
            {keepSameChildren ? (
                <div
                    className={clsx({
                        [classes.hidden]: isPending,
                    })}
                >
                    {children}
                </div>
            ) : (
                !isPending && children
            )}
        </>
    );
};

export default LoadingFrame;
